import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Command, ArrowLeft, Mail } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import * as authApi from '@/services/auth-api'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      await authApi.forgotPassword(email)
      setSuccess(true)
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } }
      const message =
        axiosError?.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Failed to send reset email')
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-4xl">
        <div className={cn("flex flex-col gap-6")}>
          <Card className="overflow-hidden p-0">
            <CardContent className="grid p-0 md:grid-cols-2">
              <div className="p-6 md:p-8">
                {success ? (
                  <FieldGroup>
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="flex aspect-square size-12 items-center justify-center rounded-lg bg-green-500 text-white mb-2">
                        <Mail className="size-6" />
                      </div>
                      <h1 className="text-2xl font-bold">Check your email</h1>
                      <p className="text-muted-foreground text-balance">
                        If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
                      </p>
                    </div>

                    <Alert className="border-green-500/50 text-green-600 dark:text-green-400">
                      <Mail className="h-4 w-4" />
                      <AlertDescription>
                        Check your inbox and spam folder for the reset link.
                      </AlertDescription>
                    </Alert>

                    <Field>
                      <Button asChild variant="outline" className="w-full">
                        <Link to="/login">
                          <ArrowLeft className="mr-2 h-4 w-4" />
                          Back to login
                        </Link>
                      </Button>
                    </Field>

                    <FieldDescription className="text-center">
                      Didn&apos;t receive the email?{' '}
                      <button
                        type="button"
                        onClick={() => setSuccess(false)}
                        className="underline underline-offset-2 hover:text-primary"
                      >
                        Try again
                      </button>
                    </FieldDescription>
                  </FieldGroup>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <FieldGroup>
                      <div className="flex flex-col items-center gap-2 text-center">
                        <div className="flex aspect-square size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground mb-2">
                          <Command className="size-6" />
                        </div>
                        <h1 className="text-2xl font-bold">Forgot password?</h1>
                        <p className="text-muted-foreground text-balance">
                          Enter your email address and we&apos;ll send you a link to reset your password.
                        </p>
                      </div>

                      {error && (
                        <Alert variant="destructive">
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>
                      )}

                      <Field>
                        <FieldLabel htmlFor="email">Email</FieldLabel>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          autoFocus
                        />
                      </Field>

                      <Field>
                        <Button type="submit" className="w-full" disabled={isLoading}>
                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Sending reset link...
                            </>
                          ) : (
                            'Send reset link'
                          )}
                        </Button>
                      </Field>

                      <Field>
                        <Button asChild variant="outline" className="w-full">
                          <Link to="/login">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to login
                          </Link>
                        </Button>
                      </Field>
                    </FieldGroup>
                  </form>
                )}
              </div>

              <div className="bg-muted relative hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=800&q=80"
                  alt="Business meeting"
                  className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
